import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Grid,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Event as EventIcon,
    DateRange as DateRangeIcon,
    CalendarToday
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { holidayAPI } from '../services/api';

const Holidays = () => {
    const [holidays, setHolidays] = useState([]);
    const [loading, setLoading] = useState(false);

    // Dialogs
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [openGenerateDialog, setOpenGenerateDialog] = useState(false);

    // Forms
    const [holidayForm, setHolidayForm] = useState({
        name: '',
        date: '',
        description: '',
        type: 'holiday',
        academicYearId: 1 // Default
    });

    const [generateForm, setGenerateForm] = useState({
        startDate: '',
        endDate: '',
        academicYearId: 1
    });

    useEffect(() => {
        fetchHolidays();
    }, []);

    const fetchHolidays = async () => {
        setLoading(true);
        try {
            const res = await holidayAPI.getAll({ academicYearId: 1 });
            setHolidays(res.data.data);
        } catch (error) {
            toast.error('Failed to fetch holidays');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateHoliday = async () => {
        try {
            await holidayAPI.create(holidayForm);
            toast.success('Holiday created');
            setOpenAddDialog(false);
            fetchHolidays();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to create holiday');
        }
    };

    const handleGenerateSundays = async () => {
        try {
            await holidayAPI.generateSundays(generateForm);
            toast.success('Sundays generated');
            setOpenGenerateDialog(false);
            fetchHolidays();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to generate');
        }
    };

    const handleDeleteHoliday = async (id) => {
        if (window.confirm('Are you sure you want to delete this holiday?')) {
            try {
                await holidayAPI.delete(id);
                toast.success('Holiday deleted');
                fetchHolidays();
            } catch (error) {
                toast.error('Failed to delete holiday');
            }
        }
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'sunday': return 'default';
            case 'holiday': return 'primary';
            case 'special': return 'secondary';
            default: return 'default';
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h4" fontWeight="bold">Holiday Calendar</Typography>
                <Box>
                    <Button
                        startIcon={<DateRangeIcon />}
                        variant="outlined"
                        sx={{ mr: 2 }}
                        onClick={() => setOpenGenerateDialog(true)}
                    >
                        Generate Sundays
                    </Button>
                    <Button
                        startIcon={<AddIcon />}
                        variant="contained"
                        onClick={() => setOpenAddDialog(true)}
                    >
                        Add Holiday
                    </Button>
                </Box>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {holidays.map((holiday) => (
                            <TableRow key={holiday.id}>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <CalendarToday fontSize="small" color="action" sx={{ mr: 1 }} />
                                        {new Date(holiday.date).toLocaleDateString()}
                                    </Box>
                                </TableCell>
                                <TableCell>{holiday.name}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={holiday.type}
                                        size="small"
                                        color={getTypeColor(holiday.type)}
                                        variant="outlined"
                                    />
                                </TableCell>
                                <TableCell>{holiday.description || '-'}</TableCell>
                                <TableCell>
                                    <IconButton size="small" color="error" onClick={() => handleDeleteHoliday(holiday.id)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {holidays.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    <Typography color="textSecondary" sx={{ py: 3 }}>
                                        No holidays found. Add one or generate Sundays.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Add Dialog */}
            <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)}>
                <DialogTitle>Add Holiday</DialogTitle>
                <DialogContent>
                    <TextField
                        margin="normal"
                        label="Date"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={holidayForm.date}
                        onChange={(e) => setHolidayForm({ ...holidayForm, date: e.target.value })}
                    />
                    <TextField
                        margin="normal"
                        label="Name"
                        fullWidth
                        value={holidayForm.name}
                        onChange={(e) => setHolidayForm({ ...holidayForm, name: e.target.value })}
                    />
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Type</InputLabel>
                        <Select
                            value={holidayForm.type}
                            onChange={(e) => setHolidayForm({ ...holidayForm, type: e.target.value })}
                        >
                            <MenuItem value="holiday">Holiday</MenuItem>
                            <MenuItem value="special">Special Event</MenuItem>
                            <MenuItem value="sunday">Sunday/Weekend</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        margin="normal"
                        label="Description"
                        fullWidth
                        multiline
                        rows={2}
                        value={holidayForm.description}
                        onChange={(e) => setHolidayForm({ ...holidayForm, description: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreateHoliday}>Save</Button>
                </DialogActions>
            </Dialog>

            {/* Generate Sundays Dialog */}
            <Dialog open={openGenerateDialog} onClose={() => setOpenGenerateDialog(false)}>
                <DialogTitle>Generate Sundays</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Automatically add all Sundays between two dates as holidays.
                    </Typography>
                    <TextField
                        margin="normal"
                        label="Start Date"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={generateForm.startDate}
                        onChange={(e) => setGenerateForm({ ...generateForm, startDate: e.target.value })}
                    />
                    <TextField
                        margin="normal"
                        label="End Date"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={generateForm.endDate}
                        onChange={(e) => setGenerateForm({ ...generateForm, endDate: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenGenerateDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleGenerateSundays}>Generate</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Holidays;
